import { Banner } from '@ingenico/react-ui/banner';
import { CoreContainer } from '@ingenico/react-ui/core-container';
import { DATAGRID_ROWS_PER_PAGE_OPTIONS, DataGridCore, ListViewContainer } from '@ingenico/react-ui/datagrid';
import { LinearProgress } from '@mui/material';
import type { ReactElement } from 'react';
import { useDeviceManagementError } from '../../hooks/useDeviceManagementError.hook';
import { ResourcesListFilters } from '../resources-list-filters/ResourcesListFilters.ctrl';
import styles from './ResourcesList.module.scss';
import type { ResourcesListVMOutput } from './ResourcesList.model';
import { HEADER_HEIGHT_RESOURCES_TO_REMOVE_FROM_DATAGRID, resourcesGridInitialState } from './ResourcesList.util';
import { EditResourceModal } from '../edit-resource-modal/EditResourceModal.ctrl';

export interface ResourcesListProps {
  readonly resourcesListVM: ResourcesListVMOutput;
}

export function ResourcesList({ resourcesListVM }: ResourcesListProps): ReactElement {
  const { getErrorMessage } = useDeviceManagementError();

  const {
    advancedSearch,
    columns,
    deleteConfirmationComponentChildren,
    errorFields,
    gridProps,
    isFirstLoading,
    noRowsProps,
    quickFiltersSearch,
    response,
    shouldDisplayGrid,
    t,
    editResourceModal,
  } = resourcesListVM;

  return (
    <>
      {isFirstLoading ? <LinearProgress /> : undefined}
      {response.error && !response.isFetching ? <Banner message={getErrorMessage(response.error)} /> : undefined}
      {errorFields ? <Banner message={getErrorMessage(errorFields)} /> : undefined}
      <CoreContainer>
        {shouldDisplayGrid ? (
          <ListViewContainer>
            <ResourcesListFilters advancedSearch={advancedSearch} quickFiltersSearch={quickFiltersSearch} t={t} />
            <DataGridCore
              apiRef={gridProps.ref}
              classes={{ row: styles.row }}
              columnBufferPx={8}
              columns={columns}
              disableMultipleColumnsSorting
              filterMode="server"
              getRowId={gridProps.getRowId}
              headerHeightPixels={HEADER_HEIGHT_RESOURCES_TO_REMOVE_FROM_DATAGRID}
              initialState={resourcesGridInitialState}
              loading={response.isFetching}
              noRowsProps={noRowsProps}
              onPaginationModelChange={gridProps.handlePaginationModelChange}
              onSortModelChange={gridProps.handleSortModelChange}
              pageSizeOptions={DATAGRID_ROWS_PER_PAGE_OPTIONS}
              paginationMode="server"
              paginationModel={{ page: gridProps.page, pageSize: gridProps.pageSize }}
              rowCount={response.rowCount}
              rows={response.data}
              sortingMode="server"
              sortModel={gridProps.sortModel}
            />
          </ListViewContainer>
        ) : undefined}
      </CoreContainer>
      {deleteConfirmationComponentChildren}
      <EditResourceModal
        onClose={editResourceModal.onClose}
        onConfirm={editResourceModal.onConfirm}
        isLoading={editResourceModal.isLoading}
        isOpen={editResourceModal.isOpen}
        errorMessage={editResourceModal.errorMessage}
        resource={editResourceModal.resource}
      />
    </>
  );
}
